import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ThemeOption = 'light' | 'dark' | 'high-contrast';
type LanguageOption = 'pt-BR' | 'en';

interface PreferencesContextValue {
  theme: ThemeOption;
  language: LanguageOption;
  setTheme: (theme: ThemeOption) => void;
  setLanguage: (language: LanguageOption) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

const THEME_KEY = 'stockflow-theme';
const LANGUAGE_KEY = 'stockflow-language';

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeOption>('light');
  const [language, setLanguageState] = useState<LanguageOption>('pt-BR');

  useEffect(() => {
    const storedTheme = localStorage.getItem(THEME_KEY) as ThemeOption | null;
    const storedLanguage = localStorage.getItem(LANGUAGE_KEY) as LanguageOption | null;

    if (storedTheme) setThemeState(storedTheme);
    if (storedLanguage) setLanguageState(storedLanguage);
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
    document.body.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast');
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, language);
  }, [language]);

  const value = useMemo(
    () => ({
      theme,
      language,
      setTheme: setThemeState,
      setLanguage: setLanguageState,
    }),
    [theme, language]
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used within PreferencesProvider');
  return context;
};
